<div class="dialog-container">
  <!-- Header (fixed) -->
  <div class="dialog-header">
    <div class="user-edit-icon">
      <svg class="dash-ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M10.5 20.5l10-10a4.95 4.95 0 0 0-7-7l-10 10a4.95 4.95 0 0 0 7 7zM8.5 8.5l7 7"/></svg>
    </div>

    <div class="header-text">
      <h2 class="addmed">{{ dialogMode === 'view' ? 'Medication Details' : (dialogMode === 'add' ? 'Add Medication' : 'Edit Medication') }}</h2>
    </div>

    <button type="button" class="close-button" (click)="onCancel()" aria-label="Close">
      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
        <path fill="currentColor"
          d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
      </svg>
    </button>
  </div>

  <!-- Body (the ONLY scrolling area) -->
  <div class="dialog-content">
    <!-- View Details: read-only rows built from the selected medication -->
    <div class="view-details" *ngIf="dialogMode === 'view'">
      <dl class="view-grid">
        <div class="view-item" *ngFor="let f of viewFields" [class.view-item--full]="f.full">
          <dt class="view-label">{{ f.label }}</dt>
          <dd class="view-value view-chips" *ngIf="f.chips?.length; else plainValue">
            <span class="view-chip" *ngFor="let t of f.chips">
              <svg class="chip-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9.5" fill="none" stroke="#5f6b7a" stroke-width="2"/><path d="M12 7v5l3 2" fill="none" stroke="#5f6b7a" stroke-width="2" stroke-linecap="round"/></svg>
              {{ t }}
            </span>
          </dd>
          <ng-template #plainValue>
            <dd class="view-value" [class.view-value--empty]="f.empty">{{ f.value }}</dd>
          </ng-template>
        </div>
      </dl>
    </div>

    <form *ngIf="dialogMode !== 'view'" [formGroup]="medForm" class="dialog-form" autocomplete="off">

      <!-- Row 1: medCondition & medicationName -->
      <div class="form-row">
        <div class="field">
          <label class="field-label" for="medCondition">Medical Condition <span class="req">*</span></label>
          <input
            id="medCondition"
            class="control"
            type="text"
            required
            formControlName="medCondition"
            list="medConditionList"
            autocomplete="off"
            placeholder="e.g., Hypertension" />
          <datalist id="medConditionList">
            <option *ngFor="let condition of filteredConditions" [value]="condition.title"></option>
          </datalist>
          <span class="error-text" *ngIf="getError('medCondition') as err">{{ err }}</span>
        </div>

        <div class="field">
          <label class="field-label" for="medicationName">Medication Name <span class="req">*</span></label>
          <input
            id="medicationName"
            class="control"
            type="text"
            required
            formControlName="medicationName"
            placeholder="e.g., Metformin" />
          <span class="error-text" *ngIf="getError('medicationName') as err">{{ err }}</span>
        </div>
      </div>

      <!-- Row 2: strength & unit -->
      <div class="form-row">
        <div class="field">
          <label class="field-label" for="strength">Strength <span class="req">*</span></label>
          <input
            id="strength"
            class="control"
            type="number"
            required
            formControlName="strength"
            min="0"
            max="10000000000"
            placeholder="e.g., 500" />
          <span class="error-text" *ngIf="getError('strength') as err">{{ err }}</span>
        </div>

        <div class="field">
          <label class="field-label" for="unit">Unit <span class="req">*</span></label>
          <select
            id="unit"
            class="control select"
            required
            formControlName="unit"
            [compareWith]="sameValue"
            [class.is-placeholder]="!medForm.get('unit')?.value">
            <option [ngValue]="''" disabled>Select unit</option>
            <option *ngFor="let unit of medUnits" [ngValue]="unit">{{ unit }}</option>
          </select>
          <span class="error-text" *ngIf="getError('unit') as err">{{ err }}</span>
        </div>
      </div>

      <!-- Row 3: Start Date & End Date -->
      <div class="form-row">
        <div class="field">
          <label class="field-label" for="startDate">Start Date <span class="req">*</span></label>
          <div class="date-wrap">
            <input
              id="startDate"
              class="control date-control"
              [matDatepicker]="startPicker"
              formControlName="startDate"
              [max]="startMax"
              readonly
              placeholder="Select date"
              (click)="startPicker.open()" />
            <mat-datepicker-toggle class="date-toggle" [for]="startPicker"></mat-datepicker-toggle>
            <mat-datepicker #startPicker></mat-datepicker>
          </div>
          <span class="error-text" *ngIf="getError('startDate') as err">{{ err }}</span>
        </div>

        <div class="field">
          <label class="field-label" for="endDate">End Date</label>
          <div class="date-wrap">
            <input
              id="endDate"
              class="control date-control"
              [matDatepicker]="endPicker"
              formControlName="endDate"
              [min]="endMin"
              readonly
              placeholder="Select date"
              (click)="endPicker.open()" />
            <mat-datepicker-toggle class="date-toggle" [for]="endPicker"></mat-datepicker-toggle>
            <mat-datepicker #endPicker></mat-datepicker>
          </div>
          <span class="error-text" *ngIf="getError('endDate') as err">{{ err }}</span>
        </div>
      </div>

      <!-- Row 4: frequency -->
      <div class="form-row form-row--full">
        <div class="field">
          <label class="field-label" for="frequency">Frequency <span class="req">*</span></label>
          <select
            id="frequency"
            class="control select"
            required
            formControlName="frequency"
            [compareWith]="sameValue"
            [class.is-placeholder]="!medForm.get('frequency')?.value">
            <option [ngValue]="''" disabled>Select frequency</option>
            <option value="everyDay">Every Day</option>
            <option value="asNeeded">As Needed</option>
          </select>
          <span class="error-text" *ngIf="getError('frequency') as err">{{ err }}</span>
        </div>
      </div>

      <!-- Dosage stepper + time chips -->
      <div class="dosage-section" *ngIf="medForm.get('frequency')?.value === 'everyDay'">
        <div class="dosage-header">
          <span class="dosage-title" id="dosageLabel">Dosage (per day) <span class="req">*</span></span>
          <div class="stepper" role="group" aria-labelledby="dosageLabel">
            <button type="button" class="stepper-btn" aria-label="Decrease dosage"
              [disabled]="(medForm.get('numberOfTimes')?.value || 1) <= 1"
              (click)="changeDosage(-1)">&minus;</button>
            <span class="stepper-value" aria-live="polite">{{ medForm.get('numberOfTimes')?.value || 1 }}</span>
            <button type="button" class="stepper-btn" aria-label="Increase dosage"
              [disabled]="(medForm.get('numberOfTimes')?.value || 1) >= 24"
              (click)="changeDosage(1)">+</button>
          </div>
        </div>
        <span class="error-text" *ngIf="getError('numberOfTimes') as err">{{ err }}</span>

        <div class="time-chips" *ngIf="medForm.get('numberOfTimes')?.value >= 1">
          <div class="time-chip" *ngFor="let tf of time_freq; let i = index">
            <svg class="chip-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9.5" fill="none" stroke="#5f6b7a" stroke-width="2"/><path d="M12 7v5l3 2" fill="none" stroke="#5f6b7a" stroke-width="2" stroke-linecap="round"/></svg>
            <input
              type="time"
              #time
              class="time-chip-input"
              [attr.aria-label]="'Time ' + (i + 1)"
              [value]="get_time(tf.time)"
              (input)="time_edit(time.value, i, medForm.get('numberOfTimes')?.value)" />
          </div>
        </div>
      </div>

      <!-- Row 5: Comment -->
      <div class="form-row form-row--full">
        <div class="field">
          <label class="field-label" for="comment">Add More Details (Optional)</label>
          <input
            id="comment"
            class="control"
            type="text"
            formControlName="comment"
            placeholder="Optional notes..." />
        </div>
      </div>

    </form>
  </div>

  <!-- Action buttons (fixed) -->
  <div class="dialog-actions" *ngIf="dialogMode !== 'view'">
    <button type="button" class="btn btn-outline" (click)="onCancel()">Cancel</button>
    <button type="button" class="btn btn-primary" (click)="onSave()">Save</button>
  </div>

  <!-- View Details actions -->
  <div class="dialog-actions" *ngIf="dialogMode === 'view'">
    <button type="button" class="btn btn-outline" (click)="onCancel()">Close</button>
    <button type="button" class="btn btn-primary" (click)="onEditFromView()">Edit</button>
  </div>
</div>